import { useDaily, useWaitingParticipants } from '@daily-co/daily-react';
import { FC, useCallback } from 'react';

import { WaitingRoomPopupUI } from './WaitingRoomPopupUI';

/**
 * Workaround for daily-react's useWaitingParticipants grantAccess/denyAccess
 * not working with prebuilt call frames (useCallFrame).
 *
 * daily-js v0.87.0 restricts updateWaitingParticipant() and
 * updateWaitingParticipants() to custom callObject instances, but the
 * underlying message channel to the prebuilt iframe supports the operation.
 * We call sendMessageToCallMachine directly to bypass that client-side check.
 */
const usePrebuiltWaitingParticipantActions = () => {
  const daily = useDaily();

  const grantAccess = useCallback(
    (id: string) => {
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      (daily as any)?.sendMessageToCallMachine({
        action: 'daily-method-update-waiting-participants',
        updatesById: { [id]: { grantRequestedAccess: true } },
      });
    },
    [daily],
  );

  const denyAccess = useCallback(
    (id: string) => {
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      (daily as any)?.sendMessageToCallMachine({
        action: 'daily-method-update-waiting-participants',
        updatesById: { [id]: { grantRequestedAccess: false } },
      });
    },
    [daily],
  );

  return { grantAccess, denyAccess };
};

export const WaitingRoomPopup: FC = () => {
  const { waitingParticipants } = useWaitingParticipants();
  const { grantAccess, denyAccess } = usePrebuiltWaitingParticipantActions();

  if (waitingParticipants.length === 0) {
    return null;
  }

  return (
    <WaitingRoomPopupUI
      participants={waitingParticipants.map((p) => ({
        id: p.id,
        name: p.name,
      }))}
      grantAccess={grantAccess}
      denyAccess={denyAccess}
    />
  );
};
